import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus, updateIdField, updateItem} from "./utils";

const initialState = {
    items: [],
    createStatus: "idle",
    fetchStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    updateStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    fetchByIdStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    deleteStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    pagination: {},
    error: null
};


//Fetch customer with discount
export const fetchCustomersWithDiscount = createAsyncThunk('customerDiscount/fetchCustomersWithDiscount',
    async ({scopeId, roleSign, page, limit} = {}) => {
        try {
            const params = new URLSearchParams();
            if (page) params.append("page", page);
            if (limit) params.append("limit", limit);

            const queryString = params.toString();
            const fullParams = queryString ? `&${queryString}` : '';

            const response = await api.get(`/${roleSign}/customer-discounts?id=${scopeId}${fullParams}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch customer with discount by ID
export const fetchCustomerWithDiscountById = createAsyncThunk('customerDiscount/fetchCustomerWithDiscountById',
    async ({roleSign, id}) => {
        try {
            const response = await api.get(`/${roleSign}/customer-discounts/${id}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Create Discount to customer
export const createDiscountToCustomer = createAsyncThunk('customerDiscount/createDiscountToCustomer',
    async ({roleSign, data}) => {
        try {
            const response = await api.post(`/${roleSign}/customer-discounts`, data);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Update customer with discount
export const updateCustomerWithDiscount = createAsyncThunk('customerDiscount/updateCustomerWithDiscount',
    async ({roleSign, discountId, data}) => {
        try {
            const response = await api.patch(`/${roleSign}/customer-discounts/${discountId}`, data);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Delete customer discount
export const deleteCustomerDiscount = createAsyncThunk('customerDiscount/deleteCustomerDiscount',
    async ({roleSign, id}) => {
        try {
            const response = await api.delete(`${roleSign}/customer-discounts/${id}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);


const discountSlice = createSlice({
    name: "discount",
    initialState,
    reducers: {
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        },
        resetUpdateStatus(state) {
            state.updateStatus = 'idle';
        },
        resetDeleteStatus(state) {
            state.deleteStatus = 'idle';
        },
        resetEditStatus(state) {
            state.editStatus = 'idle';
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Customer with Discount
            .addCase(fetchCustomersWithDiscount.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchCustomersWithDiscount.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, 'fetchStatus');
                const {data, ...pagination} = action.payload.data;
                state.items = fetchStatus === "success" ? updateIdField(data) : state.items;
                state.fetchStatus = fetchStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchCustomersWithDiscount.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })

            // Create discount to customer
            .addCase(createDiscountToCustomer.pending, (state) => {
                state.createStatus = 'loading';
            })
            .addCase(createDiscountToCustomer.fulfilled, (state, action) => {
                const {createStatus, error} = handleResponseStatus(action.payload, 'createStatus');
                state.createStatus = createStatus;
                state.error = error;
            })
            .addCase(createDiscountToCustomer.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch customer with discount by ID
            .addCase(fetchCustomerWithDiscountById.pending, (state) => {
                state.fetchByIdStatus = 'loading';
            })
            .addCase(fetchCustomerWithDiscountById.fulfilled, (state, action) => {
                const {fetchByIdStatus, error} = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchCustomerWithDiscountById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })

            // Update customer with discount
            .addCase(updateCustomerWithDiscount.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateCustomerWithDiscount.fulfilled, (state, action) => {
                const {updateStatus, error} = handleResponseStatus(action.payload, 'updateStatus');
                state.updateStatus = updateStatus;
                state.error = error;
            })
            .addCase(updateCustomerWithDiscount.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.error.message;
            })

            // Delete customer discount
            .addCase(deleteCustomerDiscount.pending, (state) => {
                state.deleteStatus = 'loading';
            })
            .addCase(deleteCustomerDiscount.fulfilled, (state, action) => {
                const {deleteStatus, error} = handleResponseStatus(action.payload, 'deleteStatus');
                const id = action.meta.arg?.id;
                if (deleteStatus === "success") {
                    state.items = state.items.filter(item => item.id !== id);
                }
                state.deleteStatus = deleteStatus;
                state.error = error;
            })
            .addCase(deleteCustomerDiscount.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.error = action.error.message;
            });

    }
});

export const {resetCreateStatus, resetUpdateStatus, resetDeleteStatus, resetEditStatus} = discountSlice.actions;
export default discountSlice.reducer;
